(function (global, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else { global.PL8WholesaleGallery = api; api.boot(); }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  const MAX_BYTES = 131072;
  const PART_KEYS = Object.freeze(['gallery_part_0', 'gallery_part_1']);
  const MAX_TOTAL_BYTES = MAX_BYTES * PART_KEYS.length + 1024;
  const HISTORY_KEY = 'pl8WholesaleGallery';
  let feature, sequence = 0, current = null, historyPending = false;
  function fail() { throw new Error('INVALID_ORDERED_MEDIA'); }
  function keys(value, names) {
    if (!value || typeof value !== 'object' || Array.isArray(value) ||
      Object.keys(value).length !== names.length || names.some(name => !Object.prototype.hasOwnProperty.call(value, name))) fail();
  }
  function text(value, max, empty = false) {
    if (typeof value !== 'string' || (!empty && !value.trim()) || value.length > max || /[\u0000-\u001f\u007f]/.test(value)) fail();
  }
  function gid(value, type) { return typeof value === 'string' && new RegExp('^gid://shopify/' + type + '/[1-9][0-9]*$').test(value); }
  function safeURL(value, kind) {
    try {
      if (typeof value !== 'string' || value.length > 2048 || /[\s<>"'\\]/.test(value)) return false;
      const url = new URL(value);
      if (url.protocol !== 'https:' || url.hostname !== 'cdn.shopify.com' || url.port || url.username || url.password || url.hash) return false;
      if (kind === 'image') return url.pathname.startsWith('/s/files/1/0013/6603/2448/') && /\.(?:png|jpe?g|webp|gif|avif)$/i.test(url.pathname);
      return url.pathname.startsWith('/3d/models/') && url.pathname.endsWith('.' + kind);
    } catch (_) { return false; }
  }
  function imageIdentity(value) {
    const url = new URL(value);
    ['width', 'height'].forEach(key => url.searchParams.delete(key));
    url.searchParams.sort();
    return url.href;
  }
  function displayImage(value) {
    const url = new URL(value);
    url.searchParams.delete('height');
    url.searchParams.set('width', '1920');
    return url.href;
  }
  function validateRecords(value, catalog, maximumBytes) {
    keys(value, ['schema_version', 'catalog_revision', 'synced_at', 'source', 'product_count', 'by_sku']);
    if (value.schema_version !== 1 || value.source !== 'shopify_product_media' ||
      value.catalog_revision !== catalog.sync_revision || !Array.isArray(catalog.entries) ||
      !value.by_sku || typeof value.by_sku !== 'object' || Array.isArray(value.by_sku) ||
      value.product_count !== catalog.entries.length || value.product_count < 1 || value.product_count > 2048 ||
      Object.keys(value.by_sku).length !== value.product_count ||
      new TextEncoder().encode(JSON.stringify(value)).length > maximumBytes) fail();
    if (typeof value.synced_at !== 'string' || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d{3})?Z$/.test(value.synced_at) || !Number.isFinite(Date.parse(value.synced_at))) fail();
    const result = Object.create(null), products = new Set(), allMedia = new Set();
    catalog.entries.forEach(item => {
      const sku = item.retailSku, entry = value.by_sku[sku];
      if (typeof sku !== 'string' || !/^[A-Z0-9]+(?:-[A-Z0-9]+)+$/.test(sku) || result[sku]) fail();
      keys(entry, ['product_id', 'retail_variant_id', 'cover_media_id', 'media_count', 'media']);
      if (!gid(entry.product_id, 'Product') || products.has(entry.product_id) ||
        !gid(entry.retail_variant_id, 'ProductVariant') || entry.retail_variant_id !== item.retailVariantId ||
        !gid(entry.cover_media_id, 'MediaImage') || !Array.isArray(entry.media) ||
        entry.media.length < 1 || entry.media.length > 250 || entry.media_count !== entry.media.length) fail();
      products.add(entry.product_id);
      let cover = -1;
      const media = entry.media.map((record, index) => {
        if (!record || record.position !== index + 1 || allMedia.has(record.id)) fail();
        text(record.alt, 1000, true);
        if (record.type === 'IMAGE') {
          keys(record, ['id', 'type', 'position', 'alt', 'src']);
          if (!gid(record.id, 'MediaImage') || !safeURL(record.src, 'image')) fail();
          if (record.id === entry.cover_media_id) {
            if (!safeURL(item.imageUrl, 'image') || imageIdentity(record.src) !== imageIdentity(item.imageUrl)) fail();
            cover = index;
          }
        } else if (record.type === 'MODEL_3D') {
          keys(record, ['id', 'type', 'position', 'alt', 'glb', 'usdz', 'poster_media_id']);
          if (!gid(record.id, 'Model3d') || !safeURL(record.glb, 'glb') ||
            !safeURL(record.usdz, 'usdz') || !gid(record.poster_media_id, 'MediaImage')) fail();
        } else fail();
        allMedia.add(record.id);
        return Object.freeze({ ...record });
      });
      if (cover < 0) fail();
      const resolved = media.map(record => {
        if (record.type !== 'MODEL_3D') return record;
        const poster = media.find(image => image.id === record.poster_media_id && image.type === 'IMAGE');
        if (!poster) fail();
        return Object.freeze({ ...record, poster: poster.src });
      });
      result[sku] = Object.freeze({ ...entry, initialIndex: cover, media: Object.freeze(resolved) });
    });
    return Object.freeze(result);
  }
  function validateManifest(value, catalog) {
    return validateRecords(value, catalog, MAX_BYTES);
  }
  function bytes(value) { return new TextEncoder().encode(JSON.stringify(value)).length; }
  function revision(value) { return typeof value === 'string' && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,99}$/.test(value); }
  function validateShards(index, parts, catalog) {
    keys(index, ['schema_version', 'source', 'catalog_revision', 'media_revision', 'synced_at', 'product_count', 'media_count', 'part_count', 'parts']);
    if (index.schema_version !== 2 || index.source !== 'shopify_product_media' ||
      !revision(index.catalog_revision) || index.catalog_revision !== catalog.sync_revision || !revision(index.media_revision) ||
      !Array.isArray(catalog.entries) || index.product_count !== catalog.entries.length || index.product_count < 1 || index.product_count > 2048 ||
      !Number.isInteger(index.media_count) || index.media_count < index.product_count || index.media_count > index.product_count * 250 ||
      index.part_count !== PART_KEYS.length || !Array.isArray(index.parts) || index.parts.length !== PART_KEYS.length ||
      !Array.isArray(parts) || parts.length !== PART_KEYS.length || bytes(index) > MAX_BYTES) fail();
    const bySku = Object.create(null);
    let productCount = 0, mediaCount = 0;
    PART_KEYS.forEach((key, partIndex) => {
      const expected = index.parts[partIndex], supplied = parts[partIndex];
      keys(expected, ['key', 'part_index', 'product_count', 'media_count']);
      keys(supplied, ['key', 'value']);
      if (expected.key !== key || expected.part_index !== partIndex || supplied.key !== key) fail();
      const part = supplied.value;
      keys(part, ['schema_version', 'catalog_revision', 'media_revision', 'part_index', 'part_count', 'product_count', 'media_count', 'by_sku']);
      if (bytes(part) > MAX_BYTES || part.schema_version !== 2 || part.catalog_revision !== index.catalog_revision ||
        part.media_revision !== index.media_revision || part.part_index !== partIndex || part.part_count !== PART_KEYS.length ||
        !Number.isInteger(part.product_count) || part.product_count < 0 || part.product_count > index.product_count ||
        !Number.isInteger(part.media_count) || part.media_count < part.product_count || part.media_count > part.product_count * 250 ||
        part.product_count !== expected.product_count || part.media_count !== expected.media_count ||
        !part.by_sku || typeof part.by_sku !== 'object' || Array.isArray(part.by_sku) || Object.keys(part.by_sku).length !== part.product_count) fail();
      let actualMedia = 0;
      Object.entries(part.by_sku).forEach(([sku, entry]) => {
        if (Object.prototype.hasOwnProperty.call(bySku, sku) || !entry || !Array.isArray(entry.media) ||
          entry.media_count !== entry.media.length) fail();
        bySku[sku] = entry;
        actualMedia += entry.media.length;
      });
      if (actualMedia !== part.media_count) fail();
      productCount += part.product_count;
      mediaCount += part.media_count;
    });
    if (productCount !== index.product_count || mediaCount !== index.media_count) fail();
    return validateRecords({ schema_version: 1, source: index.source, catalog_revision: index.catalog_revision,
      synced_at: index.synced_at, product_count: index.product_count, by_sku: bySku }, catalog, MAX_TOTAL_BYTES);
  }
  function validateConfiguredManifest(settings, catalog) {
    if (settings.galleryIndex == null) return validateManifest(settings.gallery, catalog);
    return validateShards(settings.galleryIndex, settings.galleryParts, catalog);
  }
  function ready() {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!feature) feature = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('timeout')), 20000);
      if (!window.Shopify?.loadFeatures) { clearTimeout(timeout); reject(new Error('loader')); return; }
      window.Shopify.loadFeatures([{ name: 'model-viewer', version: '1.12', onLoad(errors) {
        if (errors) { clearTimeout(timeout); reject(errors); return; }
        customElements.whenDefined('model-viewer').then(() => { clearTimeout(timeout); resolve(); });
      }}]);
    }).catch(error => { feature = null; throw error; });
    return feature;
  }
  function element(tag, className, content) {
    const node = document.createElement(tag);
    node.className = className;
    if (content !== undefined) node.textContent = content;
    return node;
  }
  function button(className, content, label) {
    const node = element('button', className, content);
    node.type = 'button';
    if (label) node.setAttribute('aria-label', label);
    return node;
  }
  function initialize(config) {
    if (config.dataset.galleryBound || typeof HTMLDialogElement === 'undefined') return;
    const root = config.closest('.pl8w');
    if (!root) return;
    let settings, catalog, ordered = null;
    try {
      settings = JSON.parse(config.textContent);
      if (!settings.galleryCopy || ['open', 'close', 'previous', 'next', 'ar', 'loading', 'imageError', 'modelError', 'arError', 'imageHint', 'modelHint', 'singleHint'].some(key => typeof settings.galleryCopy[key] !== 'string' || !settings.galleryCopy[key])) return;
      if (!window.PL8WholesaleCatalog?.read(root)) return;
      catalog = JSON.parse(root.querySelector('[data-pl8-catalog]').textContent);
      try { ordered = validateConfiguredManifest(settings, catalog); } catch (_) { /* Optional media never disables purchasing. */ }
    } catch (_) { return; }
    config.dataset.galleryBound = 'true';
    root.dataset.galleryState = ordered ? 'verified' : 'single-image-fallback';
    const copy = settings.galleryCopy, id = 'pl8w-gallery-' + (++sequence);
    const dialog = element('dialog', 'pl8w-gallery');
    dialog.id = id;
    dialog.setAttribute('aria-labelledby', id + '-title');
    dialog.setAttribute('aria-describedby', id + '-hint');
    const header = element('div', 'pl8w-gallery__header');
    const title = element('h2', 'pl8w-gallery__title'); title.id = id + '-title';
    const close = button('pl8w-gallery__close', '×', copy.close);
    header.append(title, close);
    const stage = element('div', 'pl8w-gallery__stage');
    const status = element('p', 'pl8w-gallery__status'); status.setAttribute('role', 'status');
    const nav = element('div', 'pl8w-gallery__nav');
    const previous = button('pl8w-gallery__previous', '←', copy.previous);
    const count = element('span', 'pl8w-gallery__count'); count.setAttribute('aria-live', 'polite'); count.setAttribute('aria-atomic', 'true');
    const next = button('pl8w-gallery__next', '→', copy.next);
    nav.append(previous, count, next);
    const hint = element('p', 'pl8w-gallery__hint'); hint.id = id + '-hint';
    dialog.append(header, stage, status, nav, hint);
    document.body.append(dialog);
    let active = null, renderToken = 0, timer, gesture, oldOverflow;
    const triggers = [];
    function clearMedia() {
      renderToken += 1;
      clearTimeout(timer);
      gesture = null;
      stage.replaceChildren();
      status.textContent = '';
    }
    function dismiss(updateHistory = true, restoreFocus = true) {
      if (!active) return;
      const selection = active;
      active = null;
      clearMedia();
      dialog.close();
      document.body.style.overflow = oldOverflow;
      selection.trigger.setAttribute('aria-expanded', 'false');
      if (current?.dialog === dialog) current = null;
      if (restoreFocus && selection.trigger.isConnected && !selection.card.hidden) selection.trigger.focus({ preventScroll: true });
      if (updateHistory && history.state?.[HISTORY_KEY] === selection.historyToken) {
        historyPending = true;
        history.back();
      }
    }
    async function render() {
      clearMedia();
      if (!active) return;
      const selection = active, token = renderToken, record = selection.media[selection.index];
      const valid = () => active === selection && renderToken === token;
      stage.dataset.mediaType = record.type;
      count.textContent = (selection.index + 1) + ' / ' + selection.media.length;
      previous.disabled = selection.media.length < 2;
      next.disabled = selection.media.length < 2;
      hint.textContent = record.type === 'MODEL_3D' ? copy.modelHint : (selection.media.length > 1 ? copy.imageHint : copy.singleHint);
      status.textContent = copy.loading;
      if (record.type === 'IMAGE') {
        const image = element('img', 'pl8w-gallery__image');
        image.alt = record.alt || selection.name;
        image.decoding = 'async';
        image.draggable = false;
        image.addEventListener('load', () => { if (valid()) status.textContent = ''; }, { once: true });
        image.addEventListener('error', () => { if (valid()) status.textContent = copy.imageError; }, { once: true });
        image.src = displayImage(record.src);
        stage.append(image);
        return;
      }
      try {
        await ready();
        if (!valid()) return;
        const viewer = element('model-viewer', 'pl8w-gallery__model');
        const attrs = { src: record.glb, 'ios-src': record.usdz, alt: record.alt || selection.name,
          'camera-controls': '', 'touch-action': 'pan-y', 'interaction-prompt': 'none', ar: '',
          'ar-modes': 'webxr scene-viewer quick-look', 'ar-scale': 'fixed', 'ar-placement': 'wall', poster: record.poster };
        Object.entries(attrs).forEach(([name, value]) => viewer.setAttribute(name, value));
        const ar = button('pl8w-gallery__ar', copy.ar); ar.slot = 'ar-button'; viewer.append(ar);
        viewer.addEventListener('load', () => { if (valid()) { clearTimeout(timer); status.textContent = ''; } });
        viewer.addEventListener('error', () => { if (valid()) { clearTimeout(timer); status.textContent = copy.modelError; } });
        viewer.addEventListener('ar-status', event => { if (valid() && event.detail?.status === 'failed') status.textContent = copy.arError; });
        stage.append(viewer);
        timer = setTimeout(() => { if (valid()) status.textContent = copy.modelError; }, 25000);
      } catch (_) { if (valid()) status.textContent = copy.modelError; }
    }
    function move(delta) {
      if (!active || active.media.length < 2) return;
      active.index = (active.index + delta + active.media.length) % active.media.length;
      render();
    }
    function open(card, trigger, image, entry) {
      if (historyPending || active || card.hidden) return;
      if (current) { current.dismiss(); return; }
      const inline = card.querySelector('.pl8w-model-toggle[aria-expanded="true"]');
      if (inline) inline.click();
      const media = entry?.media || [{ type: 'IMAGE', src: image.src, alt: image.alt }];
      active = { card, trigger, media, index: entry?.initialIndex || 0, name: card.querySelector('h3')?.textContent || image.alt, historyToken: id + ':' + Date.now() };
      title.textContent = active.name;
      oldOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      try { dialog.showModal(); } catch (_) { active = null; document.body.style.overflow = oldOverflow; return; }
      current = { dialog, dismiss };
      trigger.setAttribute('aria-expanded', 'true');
      try { history.pushState({ ...(history.state || {}), [HISTORY_KEY]: active.historyToken }, '', location.href); } catch (_) { /* Dialog still closes normally when history is unavailable. */ }
      close.focus({ preventScroll: true });
      render();
    }
    root.querySelectorAll('[data-catalog-sku]').forEach(card => {
      const frame = card.querySelector('.pl8w__card-image'), image = frame?.querySelector('img');
      if (!image || !safeURL(image.src, 'image')) return;
      const trigger = button('pl8w-gallery-trigger', '', (card.querySelector('h3')?.textContent || image.alt) + ': ' + copy.open);
      trigger.setAttribute('aria-haspopup', 'dialog'); trigger.setAttribute('aria-controls', id); trigger.setAttribute('aria-expanded', 'false');
      trigger.append(element('span', 'pl8w-gallery-trigger__icon', '⤢'));
      frame.append(trigger); triggers.push(trigger);
      trigger.addEventListener('click', () => open(card, trigger, image, ordered?.[card.dataset.catalogSku.replace(/^WS-/, '')]));
    });
    close.addEventListener('click', () => dismiss());
    previous.addEventListener('click', () => move(-1)); next.addEventListener('click', () => move(1));
    dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
    dialog.addEventListener('close', () => { if (active && !dialog.open) dismiss(); });
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dismiss();
    });
    dialog.addEventListener('keydown', event => {
      if (event.composedPath().some(node => node.tagName === 'MODEL-VIEWER') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') { event.preventDefault(); move(event.key === 'ArrowLeft' ? -1 : 1); }
    });
    function pointerDown(event) {
      if (!active || (event.currentTarget === stage && stage.dataset.mediaType !== 'IMAGE') || event.target.closest('button')) return;
      if (!event.isPrimary || event.button !== 0) { gesture = null; return; }
      gesture = { id: event.pointerId, x: event.clientX, y: event.clientY, time: performance.now(), target: event.currentTarget };
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch (_) { /* Synthetic/unsupported capture is harmless. */ }
    }
    function pointerUp(event) {
      const start = gesture; gesture = null;
      if (!start || start.id !== event.pointerId || start.target !== event.currentTarget) return;
      const dx = event.clientX - start.x, dy = event.clientY - start.y;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.5 && performance.now() - start.time < 1500) move(dx < 0 ? 1 : -1);
    }
    [stage, nav].forEach(node => {
      node.addEventListener('pointerdown', pointerDown);
      node.addEventListener('pointerup', pointerUp);
      node.addEventListener('pointercancel', () => { gesture = null; });
    });
    const onPopState = () => {
      historyPending = false;
      if (active && history.state?.[HISTORY_KEY] !== active.historyToken) dismiss(false);
      else if (!active && String(history.state?.[HISTORY_KEY] || '').startsWith(id + ':')) {
        const state = { ...history.state }; delete state[HISTORY_KEY]; history.replaceState(state, '', location.href);
      }
    };
    window.addEventListener('popstate', onPopState);
    const observer = new MutationObserver(() => { if (active?.card.hidden) dismiss(true, false); });
    observer.observe(root, { attributes: true, attributeFilter: ['hidden'], subtree: true });
    function unload(event) {
      if (!event.target.contains(root)) return;
      dismiss(true, false); observer.disconnect(); triggers.forEach(trigger => trigger.remove()); dialog.remove();
      window.removeEventListener('popstate', onPopState); document.removeEventListener('shopify:section:unload', unload);
      delete config.dataset.galleryBound;
    }
    document.addEventListener('shopify:section:unload', unload);
  }
  function boot() {
    window.addEventListener('popstate', () => { historyPending = false; });
    const initializeAll = () => document.querySelectorAll('[data-pl8-model-config]').forEach(initialize);
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initializeAll, { once: true }); else initializeAll();
    document.addEventListener('shopify:section:load', initializeAll);
  }
  return Object.freeze({ validateManifest, validateShards, validateConfiguredManifest, safeURL, imageIdentity, initialize, boot, MAX_BYTES, MAX_TOTAL_BYTES });
});
